import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState, useCallback, useContext } from 'react';
import Icon from '../Icon';
import RestaurantContext from '../../Context/RestaurantContext';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  display: block;

  background-color: white;

  z-index: 2;

  user-select: none !important;
`;

const SearchWrapper = styled.div`
  position: absolute;

  top: 0;
  left: 0;
  bottom: 0;

  ${(props) => {
    if (props.$searchOpened) {
      return css`
        right: 0;
      `;
    } else {
      return css`
        right: calc(100% - 40px);
      `;
    }
  }}

  transition: right 150ms ease-in-out;

  display: flex;
  align-items: center;

  z-index: 3;
  background: #ffffff;

  overflow: hidden;
  flex-wrap: nowrap;
`;

const SearchInputDiv = styled.div`
  flex-shrink: 0;
  flex-grow: 1;
  padding-left: 25px;
  padding-right: 50px;
  margin-left: 40px;
  position: relative;
  z-index: 0;
  opacity: ${(props) => (props.$searchOpened ? 1 : 0)};
  visibility: ${(props) => (props.$searchOpened ? 'visible' : 'hidden')};
  transition: opacity 0ms 80ms, visiblity 0ms 80ms;
`;

const CategoryWrapper = styled.div`
  display: flex;
  position: relative;
  white-space: nowrap;

  width: 100%;
  height: 100%;

  overflow: scroll;
  scroll-behavior: smooth;

  flex: 1 1 auto;

  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Category = styled.div`
  padding: 0 15px;
  flex-shrink: 0;
  display: inline-flex;

  justify-content: center;
  align-items: center;

  cursor: pointer;
  font-size: 15px;
  font-weight: 400;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }

  ${(props) => {
    if (props.$focused) {
      return css`
        border-bottom: 2px solid ${(props) => props.theme.colors.primary};
      `;
    }
  }}
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  height: 100%;
  background: #ffffff;
  padding-left: 15px;
  display: flex;
  align-items: center;
  z-index: 100;
`;

const SearchIcon = styled.div`
  position: relative;
  width: 25px;
  height: 25px;

  cursor: pointer;
`;

const ArrowBack = styled.div`
  position: absolute;
  right: 15px;
  top: 0;
  bottom: 0;
  width: 20px;

  &.show {
    visibility: visible;
    opacity: 1;
    max-height: 100%;
    transition: visibility 0ms 150ms, opacity 150ms 150ms, max-height 0ms 150ms;
  }
  opacity: 0;
  visibility: hidden;
  max-height: 0px;
  transition: all 0ms;

  display: flex;

  align-items: center;

  cursor: pointer;
  z-index: 4;
`;

const SearchIconOverlayGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 2;

  width: 110px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 1) 50px,
    rgba(255, 255, 255, 0) 100%
  );
`;

const SearchInput = styled.input`
  font-size: 15px;
  letter-spacing: 0.71px;
  height: 45px;
  width: 100%;
  line-height: 45px;

  border: none;
  outline: none;
`;

const CategoryAndSearchBar = ({
  categoryList,
  focusedProductGroupId,
  triggerScrollToCategoryIndex,
  onSearchTermChanged,
  ...rest
}) => {
  const categoryScrollerRef = useRef();
  const categoryNavRefs = useRef([]);
  const componentRef = useRef();
  const searchInputRef = useRef();

  const { resetMenuSearchEmit } = useContext(RestaurantContext);

  const [scrollerMarginLeft, setScrollerMarginLeft] = useState(0);
  const [searchOpened, setSearchOpened] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleClickOutside = useCallback((event) => {
    if (componentRef.current && !componentRef.current.contains(event.target)) {
      searchInputRef.current.blur();
      setSearchOpened(false);

      event.stopPropagation();
    }
  }, []);

  const closeAndResetSearch = useCallback(() => {
    searchInputRef.current.blur();
    setSearchTerm(''); // remove search input
    setSearchOpened(false);
  }, []);

  const closeOnEsc = useCallback(
    (event) => {
      // Do nothing if the event was already processed
      if (event.defaultPrevented) return;

      if (['Esc', 'Escape'].includes(event.key)) {
        closeAndResetSearch();
      }
    },
    [closeAndResetSearch],
  );

  useEffect(() => {
    if (resetMenuSearchEmit) {
      closeAndResetSearch();
    }
  }, [resetMenuSearchEmit, closeAndResetSearch]);

  useEffect(() => {
    if (searchOpened) {
      searchInputRef.current.focus({ behavior: 'smooth' });
      document.addEventListener('keydown', closeOnEsc);
    } else {
      // reset search when it gets closed
      searchInputRef.current.blur();
      setSearchTerm(''); // remove search input
      document.removeEventListener('keydown', closeOnEsc);
    }
    return () => {
      document.removeEventListener('keydown', closeOnEsc);
    };
  }, [searchOpened, closeOnEsc]);

  useEffect(() => {
    const scrollLeft = (absolutePxl) => {
      categoryScrollerRef.current.scrollTo({
        top: 0,
        left: Math.floor(absolutePxl),
        behavior: 'smooth',
      });
    };

    function scrollToNavElement(index) {
      const element = categoryNavRefs.current[index];
      const moveToLeft = element.offsetWidth / 2;
      const halfWidthContainer = categoryScrollerRef.current.offsetWidth / 2;

      var scrollerRect = categoryScrollerRef.current.getBoundingClientRect();
      const elemRect = element.getBoundingClientRect();
      const offsetFromScrollerDiv = elemRect.left - scrollerRect.left;

      const offsetX =
        categoryScrollerRef.current.scrollLeft +
        offsetFromScrollerDiv -
        halfWidthContainer +
        moveToLeft;
      scrollLeft(index === 0 ? 0 : offsetX);
    }

    // scroll to active category
    const catIndex = categoryList.findIndex((x) => x._id === focusedProductGroupId);
    if (catIndex > -1) {
      scrollToNavElement(catIndex);
    }
  }, [focusedProductGroupId, categoryList, scrollerMarginLeft]);

  useEffect(() => {
    const scrollerWidth = categoryScrollerRef.current.offsetWidth;
    const firstCatWidth = categoryNavRefs.current[0].offsetWidth;

    const marginLeft = Math.round(scrollerWidth / 2 - firstCatWidth / 2);

    if (categoryNavRefs?.current[0]) {
      setScrollerMarginLeft(marginLeft);
      categoryNavRefs.current[0].style.marginLeft = `${marginLeft}px`;
    }
  }, []);

  // callback to HoC on searchTermChange
  useEffect(() => {
    onSearchTermChanged(searchTerm);
  }, [searchTerm, onSearchTermChanged]);

  useEffect(() => {
    // set listener if search is open
    if (searchOpened) {
      // and there is no entry in search bar
      if (!searchTerm || searchTerm.length === 0) {
        document.addEventListener('click', handleClickOutside);
      } else {
        document.removeEventListener('click', handleClickOutside);
      }
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [searchTerm, searchOpened, handleClickOutside]);

  const categoryClicked = (catIndex) => {
    triggerScrollToCategoryIndex(catIndex);
  };

  const searchInputChanged = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <Wrapper ref={componentRef}>
      <>
        <SearchIconWrapper>
          <SearchIcon onClick={() => setSearchOpened((state) => !state)}>
            <Icon name="search" style={{ height: '25px', width: '25px' }} />
          </SearchIcon>
        </SearchIconWrapper>
        <SearchWrapper $searchOpened={searchOpened}>
          <SearchInputDiv $searchOpened={searchOpened}>
            <SearchInput
              ref={searchInputRef}
              type="text"
              name="search-input"
              placeholder="Suchen..."
              value={searchTerm}
              onChange={searchInputChanged}
            />
          </SearchInputDiv>
        </SearchWrapper>
        <SearchIconOverlayGradient>&nbsp;</SearchIconOverlayGradient>
        <ArrowBack className={searchOpened ? 'show' : ''} onClick={() => setSearchOpened(false)}>
          <Icon name="arrow-thick" style={{ width: '20px', transform: 'rotate(180deg)' }} />
        </ArrowBack>
      </>
      <CategoryWrapper ref={categoryScrollerRef}>
        {categoryList.map((category, index) => (
          <Category
            ref={(r) => {
              categoryNavRefs.current[index] = r;
            }}
            key={`catscroller-${category._id}`}
            $focused={
              (!focusedProductGroupId && index === 0) || focusedProductGroupId === category._id
            }
            onClick={() => categoryClicked(index)}
          >
            {category.title}
          </Category>
        ))}
      </CategoryWrapper>
    </Wrapper>
  );
};

CategoryAndSearchBar.propTypes = {
  categoryList: PropTypes.array,
  focusedProductGroupId: PropTypes.string,
  triggerScrollToCategoryIndex: PropTypes.func.isRequired,
  onSearchTermChanged: PropTypes.func,
};

export default CategoryAndSearchBar;
